import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { FaGithub, FaEnvelope } from 'react-icons/fa';

const Hero = () => {
  const typingRef = useRef(null);

  useEffect(() => {
    const typingElement = typingRef.current;
    const texts = ['資訊工程師', '電腦與通訊工程碩士', '網頁全端開發者', 'App開發者', 'AI應用開發者'];
    let textIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let typingSpeed = 150;

    const type = () => {
      const currentText = texts[textIndex];
      
      if (isDeleting) {
        typingElement.textContent = currentText.substring(0, charIndex - 1);
        charIndex--;
        typingSpeed = 50;
      } else {
        typingElement.textContent = currentText.substring(0, charIndex + 1);
        charIndex++;
        typingSpeed = 150;
      }

      // 打字完成，準備刪除
      if (!isDeleting && charIndex === currentText.length) {
        isDeleting = true;
        typingSpeed = 2000; // 停頓一段時間
      } 
      // 刪除完成，準備打下一個詞
      else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        textIndex = (textIndex + 1) % texts.length;
        typingSpeed = 500;
      }

      setTimeout(type, typingSpeed);
    };

    type();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: '0 0 15px rgba(14, 165, 233, 0.5)',
      transition: { type: 'spring', stiffness: 500 }
    },
    tap: { scale: 0.95 }
  };

  const imageVariants = {
    hidden: { scale: 0.8, opacity: 0, rotate: -10 },
    visible: {
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        delay: 0.5
      }
    }
  };

  return (
    <section id="hero" className="min-h-screen pt-20 flex items-center relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        {/* 背景圖形效果 - 圓形裝飾 */}
        <div className="absolute top-1/4 left-1/4 w-56 h-56 rounded-full bg-gradient-to-r from-primary-500/20 to-primary-700/20 animate-pulse-slow blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-72 h-72 rounded-full bg-gradient-to-r from-secondary-500/20 to-secondary-700/20 animate-pulse-slow blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="flex flex-col lg:flex-row items-center justify-between gap-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="lg:w-1/2">
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-shadow mb-4"
              variants={itemVariants}
            >
              嗨，我是<span className="text-gradient">胡晉愷</span>
            </motion.h1>
            
            <motion.div 
              className="flex items-center mb-6"
              variants={itemVariants}
            >
              <span className="text-lg sm:text-xl md:text-2xl font-medium mr-2">我是一名</span>
              <span 
                ref={typingRef} 
                className="text-lg sm:text-xl md:text-2xl font-bold text-primary-400"
              ></span>
              <span className="text-primary-400 animate-pulse">|</span>
            </motion.div>
            
            <motion.p 
              className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8 max-w-lg"
              variants={itemVariants}
            >
              國立高雄科技大學電腦與通訊工程系碩士，應用資訊科技解決實際問題。擁有網頁開發、App開發和AI應用的專業能力，注重負責、積極努力和團隊合作。
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
              variants={itemVariants}
            >
              <motion.a
                href="https://github.com/kaikaichumi"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 btn-primary"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <FaGithub /> 查看我的 GitHub
              </motion.a>
              
              <Link
                to="contact"
                spy={true}
                smooth={true}
                offset={-70}
                duration={800}
                className="cursor-pointer"
              >
                <motion.button
                  className="flex items-center gap-2 btn-outline"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <FaEnvelope /> 與我聯繫
                </motion.button>
              </Link>
            </motion.div>
          </div>
          
          <motion.div 
            className="lg:w-1/2 flex justify-center"
            variants={imageVariants}
          >
            <div className="relative">
              <motion.img
                src="/images/000.jpg"
                alt="胡晉愷"
                className="rounded-full w-64 h-64 sm:w-80 sm:h-80 object-cover border-4 border-primary-500 shadow-lg shadow-primary-500/30 z-10"
                animate={{
                  y: [0, -15, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              {/* 裝飾圓環 */}
              <div className="absolute -inset-4 rounded-full border-2 border-dashed border-primary-400 animate-spin-slow opacity-70" />
            </div>
          </motion.div>
        </motion.div>
        
        {/* 向下滾動箭頭 */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <p className="text-sm mb-2 text-gray-400">向下滾動了解更多</p>
          <motion.div
            className="w-8 h-8 border-2 border-primary-500 rounded-full flex items-center justify-center"
            animate={{
              y: [0, 12, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-primary-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
